import {
  BID_STATUS,
  CONTRACT_TYPE,
  ISO,
  MarketActivityEventType,
  MarketAlertMode,
  OFFER_STATUS,
  PRODUCT_TYPE,
  PositionType,
  PriceUnit,
  REC_PROGRAM,
  SettlementMethod,
  Side,
  TECHNOLOGY_TYPE,
  VolumeUnit,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { ApiTags } from './tags';

/**
 * The feed's filter fields, and nothing else — no session context, no
 * pagination. One-to-one with the backend
 * [`MarketActivityFilters`](../../../../backend/core/src/main/java/com/cleantrade/core/model/MarketActivityFilters.java)
 * record, which is also what an alert subscription snapshots.
 *
 * Named separately from `GetMarketActivityParams` so the alert subscription
 * types can refer to "the filters" without dragging in `sideView`,
 * `companyId`, `cursor`, and `limit`, none of which are ever part of a stored
 * snapshot.
 */
export type MarketActivityFilterFields = {
  isos?: string[];
  technologies?: string[];
  productTypes?: string[];
  contractTypes?: string[];
  applicableRecPrograms?: string[];
  hubZoneMatrixIds?: number[];
  creditProfiles?: string[];
  fixedPriceMin?: number;
  fixedPriceMax?: number;
  tenorStartDateMin?: string;
  tenorStartDateMax?: string;
  positionType?: PositionType;
  tenorMonthsMin?: number;
  tenorMonthsMax?: number;
};

export type GetMarketActivityParams = MarketActivityFilterFields & {
  sideView: Side;
  companyId: string;
  cursor?: string;
  limit?: number;
  /**
   * Drives the feed from the caller's own book instead of stated filters.
   * Mutually exclusive with `positionType`, which the backend rejects with a
   * 400 when combined with a sync mode.
   */
  syncMode?: MarketAlertMode;
  /** The side of the caller's own position. Only used with `POSITION_SYNC`. */
  syncPositionType?: PositionType;
  /** The caller's own position to sync on. Only used with `POSITION_SYNC`. */
  syncPositionId?: number;
};

export type MarketActivityEvent = {
  id: number;
  eventType: MarketActivityEventType;
  positionType: PositionType;
  positionId: number;
  positionUuid: string;
  created: string; // ISO-8601
  productTypeId: PRODUCT_TYPE;
  productTypeName: string;
  contractTypeId: CONTRACT_TYPE;
  contractTypeName: string;
  technologyId: TECHNOLOGY_TYPE;
  technologyName: string;
  isoId: ISO;
  isoName: string;
  statusId: OFFER_STATUS | BID_STATUS | null;
  statusName: string | null;
  tenorStartDate: string | null; // ISO-8601 date
  tenorMonths: number | null;
  price: number;
  /** The unit of `price`, and of any price in `changedFields`, resolved by the backend. */
  priceUnit: PriceUnit;
  /** The position's size. Null, with `volumeUnit`, while an energy offer has no P50 yet. */
  volume: number | null;
  /** The unit of `volume`, resolved by the backend. Null whenever `volume` is null. */
  volumeUnit: VolumeUnit | null;
  positionTags: string[];
  recProgramId: REC_PROGRAM;
  recProgramName: string;
  recClassId: number | null;
  recClassName: string | null;
  companyId: string | null; // UUID; null when masked
  companyName: string | null;
  projectId: string | null; // UUID; null when masked
  projectName: string | null;
  masked: boolean;
  changedFields: Record<string, { old: unknown; new: unknown }> | null;
  oldStatus: OFFER_STATUS | BID_STATUS | null;
  newStatus: OFFER_STATUS | BID_STATUS | null;
  settlementPoint: string | null;
  /** `NODAL` or `HUB`; null on a product that carries none, which counts as Hub. */
  settlementMethodId: SettlementMethod | null;
  /** An unmasked nodal offer's node, read from the offer as it is now. Null on every masked event and on every bid. */
  settlementNode: string | null;
};

export type MarketActivityFeedResponse = {
  events: MarketActivityEvent[];
  nextCursor: string | null;
};

const BASE_URL = '/api/v1/market-activity';

/**
 * Serializes everything the feed endpoint takes as a query parameter. Session
 * context (`sideView`, `companyId`) travels as headers and is excluded.
 *
 * Exported for unit testing.
 */
export function buildMarketActivitySearchParams(
  params: Omit<GetMarketActivityParams, 'sideView' | 'companyId'>
): URLSearchParams {
  const searchParams = new URLSearchParams();
  const {
    isos,
    technologies,
    productTypes,
    contractTypes,
    applicableRecPrograms,
    hubZoneMatrixIds,
    creditProfiles,
    fixedPriceMin,
    fixedPriceMax,
    tenorStartDateMin,
    tenorStartDateMax,
    positionType,
    tenorMonthsMin,
    tenorMonthsMax,
    cursor,
    limit,
    syncMode,
    syncPositionType,
    syncPositionId,
  } = params;

  isos?.forEach(iso => searchParams.append('isos', iso));
  technologies?.forEach(t => searchParams.append('technologies', t));
  productTypes?.forEach(t => searchParams.append('productTypes', t));
  contractTypes?.forEach(t => searchParams.append('contractTypes', t));
  applicableRecPrograms?.forEach(p =>
    searchParams.append('applicableRecPrograms', p)
  );
  hubZoneMatrixIds?.forEach(id =>
    searchParams.append('hubZoneMatrixIds', id.toString())
  );
  creditProfiles?.forEach(p => searchParams.append('creditProfiles', p));

  if (fixedPriceMin !== undefined)
    searchParams.append('fixedPriceMin', fixedPriceMin.toString());
  if (fixedPriceMax !== undefined)
    searchParams.append('fixedPriceMax', fixedPriceMax.toString());
  if (tenorStartDateMin)
    searchParams.append('tenorStartDateMin', tenorStartDateMin);
  if (tenorStartDateMax)
    searchParams.append('tenorStartDateMax', tenorStartDateMax);
  if (positionType) searchParams.append('positionType', positionType);
  if (tenorMonthsMin !== undefined)
    searchParams.append('tenorMonthsMin', tenorMonthsMin.toString());
  if (tenorMonthsMax !== undefined)
    searchParams.append('tenorMonthsMax', tenorMonthsMax.toString());
  if (cursor) searchParams.append('cursor', cursor);
  if (limit !== undefined) searchParams.append('limit', limit.toString());
  if (syncMode) searchParams.append('syncMode', syncMode);
  if (syncPositionType)
    searchParams.append('syncPositionType', syncPositionType);
  if (syncPositionId !== undefined)
    searchParams.append('syncPositionId', syncPositionId.toString());

  return searchParams;
}

export const marketActivityApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getMarketActivity: build.query<
      MarketActivityFeedResponse,
      GetMarketActivityParams
    >({
      query: ({ sideView, companyId, ...filterParams }) => {
        const searchParams = buildMarketActivitySearchParams(filterParams);
        const queryString = searchParams.toString();
        const suffix = queryString ? `?${queryString}` : '';

        return {
          url: `${BASE_URL}${suffix}`,
          headers: {
            'X-Side-View': sideView,
            'X-Company-Id': companyId,
          },
        };
      },
      providesTags: (result, error, { companyId, sideView }) => [
        {
          type: ApiTags.MARKET_ACTIVITY,
          id: `LIST-${companyId}-${sideView}`,
        },
      ],
    }),
  }),
});

export const { useGetMarketActivityQuery, useLazyGetMarketActivityQuery } =
  marketActivityApi;
